import { useEffect, useRef, useState, useCallback } from 'react'
import 'amazon-connect-streams'
import CTIHeader from './aws/CTIPopup'
import { CustomButton } from '@ui-kit'
import {
  getInteraction,
  hideLoader,
  setAgentStatus,
  setAssociatedRecords,
  setAwsPermission,
  setCallType,
  setIncomingCall,
  setIncomingPhoneNumber,
  setShowCCP,
  showLoader,
  AwsPermissionType,
  setProgramId,
  setInteractionId,
  setLeader,
  LeaderLockValue,
  CCPChannelMessage,
  setSiteSettings,
} from '@app-store'
import { useAppDispatch, useAppSelector } from '../utils/agent.store'
import { callRoute } from '../routes/route.constant'
import { useLocation, useNavigate } from 'react-router-dom'
import {
  editEntityData,
  getEntityDataByPhoneNumber,
} from '../apis/entityData.api'
import { FieldValues } from 'react-hook-form'
import { AWS_STRINGS } from '../utils/strings'
import {
  AWS_CONNECT_STATE,
  AWS_PHONE_REGEX,
  ENTITY_ID,
  ENTITY_NAME,
  FIVE_SEC,
  getAWSLoginUrl,
  getAwsUrl,
} from '../utils/constants'
import { AWS_ENCRYPTION_KEY } from '../utils/constants'
import { getAwsPermission } from '../apis/awsConnect.api'
import { isEmpty } from 'lodash'
import { v4 as uuidv4 } from 'uuid'
import CryptoJS from 'crypto-js'

const ENCRYPTION_KEY = AWS_ENCRYPTION_KEY

const encryptParams = (params: Record<string, string>) => {
  const jsonString = JSON.stringify(params)
  const encrypted = CryptoJS.AES.encrypt(jsonString, ENCRYPTION_KEY).toString()
  return encodeURIComponent(encrypted)
}

export const getLeaderLock = () => {
  try {
    const raw = localStorage.getItem(AWS_STRINGS.LEADER_LOCK_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as LeaderLockValue
    return parsed
  } catch {
    return null
  }
}

export const setLeaderLock = (leaderId: string) => {
  try {
    localStorage.setItem(
      AWS_STRINGS.LEADER_LOCK_KEY,
      JSON.stringify({
        leaderId,
        expiresAt: Date.now() + AWS_STRINGS.LOCK_TTL_MS,
      })
    )
  } catch (err) {
    console.error(err)
  }
}

export const clearLeaderLock = () => {
  try {
    localStorage.removeItem(AWS_STRINGS.LEADER_LOCK_KEY)
  } catch (err) {
    console.error(err)
  }
}

export const hasOngoingCall = (): boolean => {
  try {
    return localStorage.getItem(AWS_STRINGS.ONGOING_CALL_FLAG_KEY) === 'true'
  } catch {
    return false
  }
}

export const setOngoingCall = (ongoing: boolean) => {
  try {
    if (ongoing) {
      localStorage.setItem(AWS_STRINGS.ONGOING_CALL_FLAG_KEY, 'true')
    } else {
      localStorage.removeItem(AWS_STRINGS.ONGOING_CALL_FLAG_KEY)
    }
  } catch (err) {
    console.error(err)
  }
}

const extendLeaderLock = (leaderId: string) => {
  setLeaderLock(leaderId)
}

export const tryAcquireLeader = (myId: string) => {
  if (hasOngoingCall()) return false
  const lock = getLeaderLock()
  const now = Date.now()
  if (lock && lock.leaderId !== myId && lock.expiresAt > now) return false
  setLeaderLock(myId)
  const recheck = getLeaderLock()
  return recheck !== null && recheck.leaderId === myId
}

const AwsConnectCCP = () => {
  const associatedRecords = useAppSelector(
    (state) => state?.awsCall?.associatedRecords
  )
  const ccpContainer = useRef<HTMLDivElement | null>(null)
  const location = useLocation()
  const dispatch = useAppDispatch()

  const tabIdRef = useRef<string>(uuidv4())
  const channelRef = useRef<BroadcastChannel | null>(null)
  const heartbeatCheckInProgressRef = useRef(false)
  const heartbeatResponseReceivedRef = useRef(false)
  const missedBeatsRef = useRef(0)
  const heartbeatIntervalIdRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  )
  const agentStatusRef = useRef<string>('')
  const contactRef = useRef<connect.Contact | null>(null)
  const contactIdRef = useRef<string>('')
  const baseTitleRef = useRef(document.title)
  const locationRef = useRef<ReturnType<typeof useLocation>>(location)
  const isCCPInitialized = useRef<boolean>(false)
  const userId = useAppSelector((state) => state?.authUser?.user?.id)
  const isIncomingCall = useAppSelector((state) => state?.awsCall?.incomingCall)
  const callType = useAppSelector((state) => state?.awsCall?.callType)
  const programId = useAppSelector((state) => state?.awsCall?.programId)
  const interactionId = useAppSelector((state) => state?.awsCall?.interactionId)
  const showCCP = useAppSelector((state) => state?.tabUI?.showCCP)
  const awsPermission = useAppSelector((state) => state?.awsCall?.awsPermission)
  const client = useAppSelector(
    (state) => state?.authUser?.selectedClientTenant
  )
  const [isLeader, setIsLeader] = useState<boolean>(false)
  const [showLoaderState, setShowLoaderState] = useState<boolean>(false)
  const [phoneNumber, setPhoneNumber] = useState<string>('')
  const [isAepc, setIsAepc] = useState<boolean>(false)
  const [contactId, setContactId] = useState<string>('')
  const [currentQueue, setCurrentQueue] = useState<string | null>()
  const [showNotificationPopup, setIsNotificationPopup] =
    useState<boolean>(false)
  const [callerName, setCallerName] = useState<string>()
  const [callStatusHeader, setCallStatusHeader] = useState<string>(
    AWS_STRINGS.AMAZON_CONNECT
  )
  const [program, setProgram] = useState<string>()
  const isIncomingCallRef = useRef(isIncomingCall)
  const isLeaderRef = useRef(isLeader)

  const getCallerName = (data: FieldValues): string => {
    const entityKeys = Object.keys(data)
    if (entityKeys.length > 1) {
      setCallerName(AWS_STRINGS.MULTIPLE_RECORDS)
      return AWS_STRINGS.MULTIPLE_RECORDS
    }
    const entity = data[entityKeys[0]]
    if (!entity.records || entity.records.length !== 1) {
      setCallerName(AWS_STRINGS.MULTIPLE_RECORDS)
      return AWS_STRINGS.MULTIPLE_RECORDS
    }
    const name = entity.records[0].name || AWS_STRINGS.UNKNOWN_CALLER
    setCallerName(name)
    return name
  }

  const formatUSNumber = (phone: string): string => {
    const cleaned = phone.replace(/\D/g, '')
    const match = cleaned.match(AWS_PHONE_REGEX)
    if (match) {
      return `+1 ${match[1]}-${match[2]}-${match[3]}`
    }
    return phone
  }

  const fetchAssociatedRecords = async (
    phoneNumber: string
  ): Promise<string | undefined> => {
    try {
      const formattedNumber = phoneNumber.replace('+', '%2B')
      const res = await getEntityDataByPhoneNumber(formattedNumber)
      if (res.data) {
        const name = getCallerName(res.data)
        dispatch(setAssociatedRecords(res.data))
        return name
      }
    } catch (err) {
      setCallerName(AWS_STRINGS.UNKNOWN_CALLER)
      dispatch(setAssociatedRecords({}))
      console.error(err)
      return AWS_STRINGS.UNKNOWN_CALLER
    }
    return undefined
  }

  const navigate = useNavigate()

  const updatePersistedIncomingParams = (
    incoming: boolean,
    incomingPhone?: string | null
  ) => {
    try {
      const params = new URLSearchParams(location.search)
      if (incoming) {
        const paramData: Record<string, string> = {
          incomingCall: '1',
        }
        if (incomingPhone) {
          paramData.incomingPhoneNumber = incomingPhone
        }
        if (callType) {
          paramData.callType = callType
        }
        if (programId) {
          paramData.programId = programId
        }
        if (interactionId) {
          paramData.interactionId = interactionId
        }

        if (associatedRecords && Object.keys(associatedRecords).length > 0) {
          paramData.associatedRecords = JSON.stringify(associatedRecords)
        }

        paramData.timestamp = Date.now().toString()
        const encrypted = encryptParams(paramData)
        params.set('data', encrypted)
      } else {
        params.delete('data')
      }
      navigate(
        { pathname: location.pathname, search: params.toString() },
        {
          replace: true,
        }
      )
    } catch (err) {
      console.error('Unable to update incoming params', err)
    }
  }

  const postMessage = useCallback((msg: CCPChannelMessage) => {
    try {
      channelRef.current?.postMessage(msg)
    } catch (err) {
      console.error(err)
    }
  }, [])

  const dismissNotification = useCallback(() => {
    setIsNotificationPopup(false)
    postMessage({ type: 'NOTIFICATION_DISMISSED' })
  }, [postMessage])

  const handleCallEvent = async (state: string, contact: connect.Contact) => {
    let phone = ''
    let inbound = false
    let currentType = ''
    let attrs: Record<string, { value?: string }> | undefined
    try {
      attrs = contact.getAttributes() as Record<string, { value?: string }>
      inbound = Boolean(attrs?.Flag_EP_CallType?.value === AWS_STRINGS.INBOUND)
      setProgram(attrs?.Flag_EP_Program?.value)
      setIsAepc(Boolean(attrs?.Flag_EP_AEPC?.value?.toLowerCase() === 'true'))
      dispatch(setCallType(attrs?.Flag_EP_CallSubType?.value || ''))
      dispatch(setProgramId(attrs?.Flag_EP_ProgramId?.value || ''))
      currentType = attrs?.Flag_EP_CallSubType?.value || ''
      if (attrs?.Flag_EP_CallerNumber?.value) {
        phone = attrs.Flag_EP_CallerNumber.value ?? ''
      } else if (contact.getActiveInitialConnection()?.getEndpoint()) {
        phone =
          contact.getActiveInitialConnection()?.getEndpoint()?.phoneNumber ||
          phone
      }
    } catch (err) {
      console.error(err)
    }
    const queue = contact.getQueue()

    switch (state) {
      case AWS_CONNECT_STATE.INCOMING:
      case AWS_CONNECT_STATE.CONNECTING:
        if (inbound) {
          contactRef.current = contact
          setOngoingCall(true)
          setShowLoaderState(true)
          setIsNotificationPopup(true)
          const formattedPhone = formatUSNumber(phone)
          setPhoneNumber(formattedPhone)
          setCurrentQueue(queue?.name)
          const resolvedCallerName = isEmpty(callerName)
            ? await fetchAssociatedRecords(phone)
            : callerName
          setCallerName(resolvedCallerName ?? '')
          let statusHeader = AWS_STRINGS.INCOMING_CALL
          if (currentType === AWS_STRINGS.VOICEMAIL) {
            statusHeader = AWS_STRINGS.INCOMING_VOICEMAIL
          } else if (
            currentType === AWS_STRINGS.CCB ||
            currentType === AWS_STRINGS.CALL_BACK
          ) {
            statusHeader = AWS_STRINGS.INCOMING_CCB
          }
          setCallStatusHeader(statusHeader)
          postMessage({
            type: 'CALL_STATUS_HEADER_CHANGED',
            callStatusHeader: statusHeader,
          })
          dispatch(setShowCCP(true))
          dispatch(setIncomingPhoneNumber(phone))
          dispatch(setIncomingCall(true))
          updatePersistedIncomingParams(true, phone)
          setShowLoaderState(false)
          postMessage({
            type: 'INCOMING_CALL',
            contactId: contact.contactId,
            phone: formattedPhone,
            callerName: resolvedCallerName ?? undefined,
            program: attrs?.Flag_EP_Program?.value,
            currentQueue: queue?.name ?? null,
            callStatusHeader: statusHeader,
            isAepc: Boolean(
              attrs?.Flag_EP_AEPC?.value?.toLowerCase() === 'true'
            ),
          })
        } else {
          dismissNotification()
          dispatch(setShowCCP(true))
          setCallStatusHeader(AWS_STRINGS.ONGOING_CALL)
          postMessage({
            type: 'CALL_STATUS_HEADER_CHANGED',
            callStatusHeader: AWS_STRINGS.ONGOING_CALL,
          })
          dispatch(setIncomingCall(true))
        }
        break
      case AWS_CONNECT_STATE.OUTGOING:
        dismissNotification()
        dispatch(setShowCCP(true))
        setCallStatusHeader(AWS_STRINGS.ONGOING_CALL)
        postMessage({
          type: 'CALL_STATUS_HEADER_CHANGED',
          callStatusHeader: AWS_STRINGS.ONGOING_CALL,
        })
        dispatch(setIncomingCall(true))
        break
      case AWS_CONNECT_STATE.CONNECTED:
        setCallStatusHeader(AWS_STRINGS.ONGOING_CALL)
        postMessage({
          type: 'CALL_STATUS_HEADER_CHANGED',
          callStatusHeader: AWS_STRINGS.ONGOING_CALL,
        })
        break
      case AWS_CONNECT_STATE.MISSED:
        dismissNotification()
        dispatch(setIncomingCall(false))
        dispatch(setIncomingPhoneNumber(null))
        updatePersistedIncomingParams(false)
        setCallerName('')
        break
      case AWS_CONNECT_STATE.ACW:
      case AWS_CONNECT_STATE.ENDED:
      case AWS_CONNECT_STATE.DESTROYED:
        dispatch(setIncomingCall(false))
        dispatch(setIncomingPhoneNumber(null))
        updatePersistedIncomingParams(false)
        break
      default:
        break
    }

    setTimeout(() => null, FIVE_SEC)
  }

  const saveContactId = async (
    interactionId: string,
    contactIdForSave: string
  ) => {
    try {
      await editEntityData(
        ENTITY_NAME.COMMUNICATION,
        ENTITY_ID.COMMUNICATION,
        1, // Random layoutId
        {
          aws_call_id: contactIdForSave,
        },
        interactionId
      )
    } catch (err) {
      console.error(err)
    }
  }

  const handleCreateInteraction = async (contactIdForSave?: string) => {
    const cid = contactIdForSave ?? contactIdRef.current ?? contactId
    try {
      dispatch(showLoader())
      const response = await getInteraction(userId ?? '')
      if (response?.data?.id) {
        const interactionId = String(response.data.id)
        saveContactId(response?.data?.id, cid)
        dispatch(setInteractionId(interactionId))
        updatePersistedIncomingParams(true)
        window.open(
          `/${callRoute}/create/${Number(response.data.id)}${location.search}`,
          '_blank'
        )
      }
    } catch (error) {
      console.error(error)
    } finally {
      dispatch(hideLoader())
    }
  }

  const onAcceptClick = async () => {
    if (isLeaderRef.current && contactRef.current) {
      try {
        const cid = contactRef.current.contactId
        contactRef.current.accept()
        channelRef.current?.postMessage({
          type: 'ACCEPT_CALL_ACK',
          contactId: cid,
          requestTabId: tabIdRef.current,
        })
        await handleCreateInteraction(cid)
        dismissNotification()
      } catch (err) {
        console.error(err)
      }
    } else {
      channelRef.current?.postMessage({
        type: 'ACCEPT_CALL_REQUEST',
        contactId: contactIdRef.current,
        requestTabId: tabIdRef.current,
      })
    }
  }

  useEffect(() => {
    if (
      !isLeader ||
      !ccpContainer.current ||
      awsPermission?.value !== 'true' ||
      isCCPInitialized.current
    )
      return

    isCCPInitialized.current = true

    connect.core.initCCP(ccpContainer.current, {
      ccpUrl: getAwsUrl(),
      loginPopup: true,
      loginPopupAutoClose: true,
      loginUrl: getAWSLoginUrl(),
      region: AWS_STRINGS.REGION,
      softphone: { allowFramedSoftphone: true },
      loginOptions: { autoClose: true },
    })

    connect.agent((agent) => {
      const currentState = agent.getState()
      const status = currentState?.name || ''
      agentStatusRef.current = status
      dispatch(setAgentStatus(status))
      postMessage({ type: 'AGENT_STATUS_CHANGED', status })

      agent.onStateChange((stateChange) => {
        const newStatus = stateChange.newState || ''
        agentStatusRef.current = newStatus
        dispatch(setAgentStatus(newStatus))
        postMessage({ type: 'AGENT_STATUS_CHANGED', status: newStatus })
      })
    })

    connect.contact((contact) => {
      setContactId(contact.contactId)
      contact.onIncoming(() => {
        dispatch(setShowCCP(false))
        handleCallEvent(AWS_CONNECT_STATE.CONNECTING, contact)
      })

      contact.onConnecting(() => {
        dispatch(setShowCCP(false))
        handleCallEvent(AWS_CONNECT_STATE.CONNECTING, contact)
      })

      contact.onAccepted(() => {
        handleCallEvent(AWS_CONNECT_STATE.OUTGOING, contact)
      })

      contact.onMissed(() => {
        handleCallEvent(AWS_CONNECT_STATE.MISSED, contact)
      })

      contact.onConnected(() => {
        dispatch(setShowCCP(true))
        handleCallEvent(AWS_CONNECT_STATE.CONNECTED, contact)
      })

      contact.onACW(() => {
        handleCallEvent(AWS_CONNECT_STATE.ACW, contact)
      })

      contact.onEnded(() => {
        handleCallEvent(AWS_CONNECT_STATE.ENDED, contact)
      })

      contact.onDestroy(() => {
        contactRef.current = null
        setOngoingCall(false)
        handleCallEvent(AWS_CONNECT_STATE.DESTROYED, contact)
        setCallStatusHeader(AWS_STRINGS.AMAZON_CONNECT)
        postMessage({
          type: 'CALL_STATUS_HEADER_CHANGED',
          callStatusHeader: AWS_STRINGS.AMAZON_CONNECT,
        })
        setCurrentQueue(null)
        setPhoneNumber('')
        setCallerName('')
        dismissNotification()
        dispatch(setCallType(''))
        dispatch(setProgramId(''))
        dispatch(setIncomingCall(false))
        dispatch(setIncomingPhoneNumber(null))
        dispatch(setAssociatedRecords({}))
        dispatch(setInteractionId(''))
      })
    })
  }, [awsPermission, isLeader])

  useEffect(() => {
    if (typeof BroadcastChannel === 'undefined') {
      return
    }
    const channel = new BroadcastChannel(AWS_STRINGS.CCP_CHANNEL_NAME)
    channelRef.current = channel

    const onMessage = (e: MessageEvent<CCPChannelMessage>) => {
      const msg = e.data

      switch (msg.type) {
        case 'INCOMING_CALL': {
          if (isLeaderRef.current) return

          contactIdRef.current = msg.contactId
          setContactId(msg.contactId)
          setPhoneNumber(msg.phone)
          setCallerName(msg.callerName ?? '')
          setProgram(msg.program)
          setCurrentQueue(msg.currentQueue ?? null)
          setCallStatusHeader(msg.callStatusHeader)
          setIsAepc(msg.isAepc)
          setIsNotificationPopup(true)

          dispatch(setShowCCP(true))
          dispatch(setIncomingPhoneNumber(msg.phone))
          dispatch(setIncomingCall(true))

          fetchAssociatedRecords(msg.phone)
          break
        }

        case 'ACCEPT_CALL_REQUEST': {
          if (!isLeaderRef.current) return

          if (contactRef.current?.contactId === msg.contactId) {
            try {
              const cid = contactRef.current.contactId
              contactRef.current.accept()

              channel.postMessage({
                type: 'ACCEPT_CALL_ACK',
                contactId: cid,
                requestTabId: msg.requestTabId,
              })
            } catch (err) {
              console.error(err)
            }
          }
          break
        }

        case 'ACCEPT_CALL_ACK': {
          if (isLeaderRef.current) return
          if (msg.contactId !== contactIdRef.current) break
          dismissNotification()
          if (msg.requestTabId === tabIdRef.current) {
            handleCreateInteraction(msg.contactId)
          }
          break
        }

        case 'LEADER_HEARTBEAT_REQUEST': {
          if (isLeaderRef.current) {
            extendLeaderLock(tabIdRef.current)
            channel.postMessage({
              type: 'LEADER_HEARTBEAT_RESPONSE',
              leaderId: tabIdRef.current,
            })
          }
          break
        }

        case 'LEADER_HEARTBEAT_RESPONSE': {
          heartbeatResponseReceivedRef.current = true
          missedBeatsRef.current = 0
          break
        }

        case 'LEADER_ANNOUNCE': {
          if (
            msg.leaderId &&
            msg.leaderId !== tabIdRef.current &&
            isLeaderRef.current
          ) {
            setIsLeader(false)
            dispatch(setLeader(false))
          }
          break
        }

        case 'NOTIFICATION_DISMISSED': {
          setIsNotificationPopup(false)
          break
        }

        case 'AGENT_STATUS_CHANGED': {
          dispatch(setAgentStatus(msg.status ?? ''))
          break
        }

        case 'AGENT_STATUS_REQUEST': {
          if (isLeaderRef.current) {
            channel.postMessage({
              type: 'AGENT_STATUS_CHANGED',
              status: agentStatusRef.current ?? '',
            })
          }
          break
        }

        case 'CALL_STATUS_HEADER_CHANGED': {
          setCallStatusHeader(msg.callStatusHeader ?? '')
          break
        }

        default: {
          console.warn(msg)
        }
      }
    }

    channel.addEventListener('message', onMessage)

    const requestStatusTimer = setTimeout(
      () => channel.postMessage({ type: 'AGENT_STATUS_REQUEST' }),
      300
    )

    const syncFromLock = () => {
      const myId = tabIdRef.current
      const lock = getLeaderLock()
      const now = Date.now()
      if (lock && lock.leaderId === myId && lock.expiresAt > now) {
        setLeaderLock(myId)
        setIsLeader(true)
        dispatch(setLeader(true))
        channel.postMessage({ type: 'LEADER_ANNOUNCE', leaderId: myId })
        return
      }
      if (lock && lock.leaderId !== myId && lock.expiresAt > now) {
        if (!heartbeatCheckInProgressRef.current) {
          heartbeatCheckInProgressRef.current = true
          missedBeatsRef.current = 0
          const runHeartbeatCheck = () => {
            channel.postMessage({ type: 'LEADER_HEARTBEAT_REQUEST' })
            heartbeatResponseReceivedRef.current = false
            setTimeout(() => {
              if (!heartbeatResponseReceivedRef.current) {
                missedBeatsRef.current += 1
                if (
                  missedBeatsRef.current >=
                  AWS_STRINGS.MISSED_BEATS_BEFORE_TAKEOVER
                ) {
                  if (heartbeatIntervalIdRef.current !== null) {
                    clearInterval(heartbeatIntervalIdRef.current)
                    heartbeatIntervalIdRef.current = null
                  }
                  heartbeatCheckInProgressRef.current = false
                  setOngoingCall(false)
                  clearLeaderLock()
                  if (tryAcquireLeader(myId)) {
                    setIsLeader(true)
                    dispatch(setLeader(true))
                    channel.postMessage({
                      type: 'LEADER_ANNOUNCE',
                      leaderId: myId,
                    })
                  } else {
                    setIsLeader(false)
                    dispatch(setLeader(false))
                  }
                  return
                }
              }
            }, AWS_STRINGS.HEARTBEAT_WAIT_MS)
          }
          runHeartbeatCheck()
          heartbeatIntervalIdRef.current = setInterval(
            runHeartbeatCheck,
            AWS_STRINGS.HEARTBEAT_INTERVAL_MS
          )
        }
        return
      }
      if (tryAcquireLeader(myId)) {
        setIsLeader(true)
        dispatch(setLeader(true))
        channel.postMessage({ type: 'LEADER_ANNOUNCE', leaderId: myId })
      } else {
        setIsLeader(false)
        dispatch(setLeader(false))
      }
    }
    syncFromLock()
    const t = setInterval(syncFromLock, AWS_STRINGS.POLL_INTERVAL_MS)

    return () => {
      clearTimeout(requestStatusTimer)
      if (heartbeatIntervalIdRef.current !== null) {
        clearInterval(heartbeatIntervalIdRef.current)
        heartbeatIntervalIdRef.current = null
      }
      channel.removeEventListener('message', onMessage)
      channel.close()
      channelRef.current = null
      clearInterval(t)
    }
  }, [dispatch])

  useEffect(() => {
    isLeaderRef.current = isLeader
  }, [isLeader])

  //ToDo Later
  // useEffect(() => {
  //   if (!isLeader) return
  //   const handleLeaderTabClose = () => {
  //     debugger
  //     setOngoingCall(false)
  //     clearLeaderLock()
  //     const contact = contactRef.current
  //     try {
  //       const conn = contact?.getAgentConnection?.()
  //       if (conn?.destroy) {
  //         conn.destroy()
  //       }
  //     } catch (err) {
  //       console.error(err)
  //     }
  //   }
  //   // window.addEventListener('beforeunload', handleLeaderTabClose)
  //   window.addEventListener('pagehide', handleLeaderTabClose)
  //   return () => {
  //     // window.removeEventListener('beforeunload', handleLeaderTabClose)
  //     window.removeEventListener('pagehide', handleLeaderTabClose)
  //   }
  // }, [isLeader])

  useEffect(() => {
    contactIdRef.current = contactId
  }, [contactId])

  useEffect(() => {
    isIncomingCallRef.current = isIncomingCall
  }, [isIncomingCall])

  useEffect(() => {
    const fetchAwsPermission = async () => {
      dispatch(showLoader())
      try {
        const res = await getAwsPermission()
        if (res?.data) {
          const awsPermission = res.data.find(
            (list: AwsPermissionType) =>
              list.key === AWS_STRINGS.CTI_PERMISSION_KEY
          )
          dispatch(setSiteSettings(res.data))
          dispatch(setAwsPermission(awsPermission))
        }
      } catch (err) {
        console.error(err)
      } finally {
        dispatch(hideLoader())
      }
    }

    fetchAwsPermission()
  }, [client])

  useEffect(() => {
    locationRef.current = location
  }, [location])

  useEffect(() => {
    if (awsPermission?.value === 'true') {
      baseTitleRef.current = document.title.replace(/^📞\s*/, '').trim()
    }
  }, [awsPermission])

  useEffect(() => {
    if (awsPermission?.value !== 'true') return
    if (isLeader) {
      document.title = `📞 ${baseTitleRef.current || 'Leader tab'}`
    } else {
      document.title = baseTitleRef.current || document.title
    }
    return () => {
      document.title = baseTitleRef.current || document.title
    }
  }, [isLeader, awsPermission])

  // Not working: TODO
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isLeaderRef.current) {
        clearLeaderLock()
        e.preventDefault()
        e.returnValue = ''
        return e.returnValue
      }
    }
    const handlePageHide = () => {
      if (isLeaderRef.current) clearLeaderLock()
    }
    window.addEventListener('beforeunload', handleBeforeUnload)
    window.addEventListener('pagehide', handlePageHide)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      window.removeEventListener('pagehide', handlePageHide)
    }
  }, [])

  const showCcpArea = showNotificationPopup || (isLeader && showCCP)
  const showCcpContainer = isLeader && showCCP && !showNotificationPopup

  return (
    <div
      style={{
        display: showCcpArea ? 'block' : 'none',
      }}
    >
      <CTIHeader callStatus={callStatusHeader} showLoader={showLoaderState}>
        {showNotificationPopup && (
          <div className="p-4">
            <div className="flex flex-row justify-between items-center">
              <span className="font-primary font-semibold text-xl">
                {callerName}
              </span>
              <span className="font-primary font-bold text-sm text-accent-600">
                {phoneNumber}
              </span>
            </div>
            <div className="mt-3 p-3 bg-secondary-50 rounded-lg">
              <div className="flex flex-col">
                <span className="font-primary text-xs font-light text-accent-600">
                  {AWS_STRINGS.PROGRAM}
                </span>
                <span className="font-primary text-sm font-normal">
                  {program}
                </span>
              </div>
              <div className="flex flex-col mt-3">
                <span className="font-primary text-xs font-light text-accent-600">
                  {AWS_STRINGS.QUEUE}
                </span>
                <span className="font-primary text-sm font-normal">
                  {currentQueue}
                </span>
              </div>
            </div>
            {isAepc && (
              <div className="mt-3 bg-error-100 py-2 pl-3 rounded-md border-l-error-600 border-l-4 font-primary text-sm font-normal">
                {AWS_STRINGS.AE_PC_NOTIFICATION}
              </div>
            )}
            <div className="flex flex-row-reverse">
              <CustomButton
                className="mt-4"
                horizontalPaddingClasses="px-11"
                onClick={onAcceptClick}
                isFilled
              >
                {AWS_STRINGS.ACCEPT}
              </CustomButton>
            </div>
          </div>
        )}
        <div
          ref={ccpContainer}
          style={{
            height: showCcpContainer ? 500 : 0,
            minHeight: showCcpContainer ? 500 : 0,
            overflow: 'hidden',
            visibility: showCcpContainer ? 'visible' : 'hidden',
            display: showCcpContainer ? 'block' : 'none',
            zIndex: 9999,
            borderRadius: '10px',
          }}
          aria-hidden={!showCcpContainer}
        />
      </CTIHeader>
    </div>
  )
}

export default AwsConnectCCP
